/** @jsxImportSource @emotion/react */

import {ErrorMessages, requestPasswordReset, resetPassword} from "api/auth";
import Button from "components/Button";
import {Anchor, H1, Text} from "components/elements";
import Link from "components/Link";
import {useMutation} from "react-query";
import {css} from "@emotion/react";
import SplitScreen from "components/SplitScreen";
import buildingsImage from "assets/images/buildings.jpg";
import {useForm} from "react-hook-form";
import {ReactComponent as AeigLogo} from "assets/images/AeigLogo.svg";
import AlertBar from "components/AlertBar";
import ControlledTextInput from "components/ControlledTextInput";
import {validatePasswordStrength} from "utils/auth";
import {useEffect} from "react";
import {getHtmlTitle} from "utils";

const formStyle = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
`;

interface RequestResetFormFields {
  email: string;
}

interface ResetPasswordFormFields {
  password: string;
  confirmPassword: string;
}

export default function PasswordReset({token}: {token?: string}) {
  useEffect(() => {
    window.document.title = getHtmlTitle("Reset Password");
  });
  return (
    <SplitScreen imageurl={buildingsImage}>
      {token ? <ResetPasswordForm token={token} /> : <RequestResetForm />}
    </SplitScreen>
  );
}

function RequestResetForm() {
  const {handleSubmit, control} = useForm<RequestResetFormFields>();
  const requestResetMutation = useMutation((formData: RequestResetFormFields) =>
    requestPasswordReset(formData.email.trim())
  );

  if (requestResetMutation.isSuccess) {
    return (
      <div css={formStyle}>
        <Anchor href={process.env.REACT_APP_AEIG_INSURANCE_WEBSITE_LINK}>
          <AeigLogo />
        </Anchor>
        <H1>Check your email</H1>
        <Text size={1}>
          If an account exists for that email address, we have sent a link to
          reset your password. The link will expire shortly, so please use it
          soon.
        </Text>
        <div>
          <Link to="/login">Back to Login</Link>
        </div>
      </div>
    );
  }

  return (
    <form
      css={formStyle}
      onSubmit={handleSubmit((formData) =>
        requestResetMutation.mutateAsync(formData)
      )}
      onFocus={requestResetMutation.reset}
    >
      <Anchor href={process.env.REACT_APP_AEIG_INSURANCE_WEBSITE_LINK}>
        <AeigLogo />
      </Anchor>
      <H1>Reset Password</H1>
      <Text size={1}>
        Enter the email address for your account and we will send you a link to
        reset your password.
      </Text>
      <ControlledTextInput
        name={"email"}
        control={control}
        label={"Email"}
        type="email"
        required
        rules={{required: true}}
      />
      {requestResetMutation.isError && (
        <AlertBar status="error">
          Something went wrong. Please try again.
        </AlertBar>
      )}
      <Button
        fullwidth
        size="large"
        type="submit"
        isLoading={requestResetMutation.isLoading}
      >
        Send Reset Link
      </Button>
      <div>
        <Link to="/login">Back to Login</Link>
      </div>
    </form>
  );
}

function ResetPasswordForm({token}: {token: string}) {
  const {getValues, handleSubmit, control} = useForm<ResetPasswordFormFields>();
  const resetPasswordMutation = useMutation(
    (formData: ResetPasswordFormFields) =>
      resetPassword(token, formData.password)
  );

  if (resetPasswordMutation.isSuccess) {
    return (
      <div css={formStyle}>
        <Anchor href={process.env.REACT_APP_AEIG_INSURANCE_WEBSITE_LINK}>
          <AeigLogo />
        </Anchor>
        <H1>Password Updated</H1>
        <Text size={1}>
          Your password has been reset. You can now log in with your new
          password.
        </Text>
        <div>
          <Link to="/login">Go to Login</Link>
        </div>
      </div>
    );
  }

  const error = resetPasswordMutation.error;
  const errorMessage =
    error === ErrorMessages.INVALID_PASSWORD_RESET_TOKEN
      ? "This password reset link is invalid or has expired. Please request a new one."
      : typeof error === "string" && !error.includes("Traceback")
      ? error
      : "Something went wrong. Please try again.";

  return (
    <form
      css={formStyle}
      onSubmit={handleSubmit((formData) =>
        resetPasswordMutation.mutateAsync(formData)
      )}
      onFocus={resetPasswordMutation.reset}
    >
      <Anchor href={process.env.REACT_APP_AEIG_INSURANCE_WEBSITE_LINK}>
        <AeigLogo />
      </Anchor>
      <H1>Choose a New Password</H1>
      <ControlledTextInput
        type="password"
        name={"password"}
        control={control}
        label={"New Password"}
        rules={{
          required: true,
          validate: (value: string) => {
            const rulesResultList = validatePasswordStrength(value);
            return rulesResultList.length === 0 || rulesResultList.join("; ");
          },
        }}
      />
      <ControlledTextInput
        type="password"
        name={"confirmPassword"}
        control={control}
        label={"Confirm New Password"}
        required
        rules={{
          validate: (value: string) =>
            value === getValues("password") || "The passwords do not match",
          required: true,
        }}
      />
      {resetPasswordMutation.isError && (
        <AlertBar status="error">
          {errorMessage}
          {error === ErrorMessages.INVALID_PASSWORD_RESET_TOKEN && (
            <>
              {" "}
              <Link to="/password/reset">Request a new link</Link>
            </>
          )}
        </AlertBar>
      )}
      <Button
        fullwidth
        size="large"
        type="submit"
        isLoading={resetPasswordMutation.isLoading}
      >
        Reset Password
      </Button>
      <div>
        <Link to="/login">Back to Login</Link>
      </div>
    </form>
  );
}
